import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import type {
  EmailProviderConnection,
  EmailProviderConnectionScope,
  EmailThreadCompanyConnection,
  EmailThreadCompanyConnectionWithCompany,
} from "@/types/emailThread";

async function invoke<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke("email-thread-api", {
    body: { action, ...params },
  });
  if (error) throw await toEdgeFunctionFailure(error, "Unknown error");
  return data as T;
}

/** Thin HTTP client over `email-thread-api` (issue #290) — mirrors `MessagingApiClient`'s shape. */
export class EmailThreadApiClient {
  async initiateGmailOAuth(opts: {
    connectionScope: EmailProviderConnectionScope;
    companyId?: string;
    /** This app's own /gmail/callback page. Only its ORIGIN is read by the backend, to pick this
     * deployment from the CONSULTANT_SITE_URLS allowlist so Google's return lands back here. */
    callbackUrl?: string;
  }): Promise<{ authUrl: string }> {
    return invoke("initiateGmailOAuth", opts);
  }

  async getEmailProviderStatus(connectionId: string): Promise<{ connection: EmailProviderConnection | null }> {
    return invoke("getEmailProviderStatus", { connectionId });
  }

  /** `companyId` omitted/undefined lists platform-wide connections (platform-admin view). */
  async listConnections(companyId?: string): Promise<{ connections: EmailProviderConnection[] }> {
    return invoke("listConnections", companyId ? { companyId } : {});
  }

  async disconnectGmail(connectionId: string): Promise<{ success: boolean }> {
    return invoke("disconnectGmail", { connectionId });
  }

  async listPendingConnections(companyId: string): Promise<{ connections: EmailThreadCompanyConnection[] }> {
    return invoke("listPendingConnections", { companyId });
  }

  async listAllPendingConnections(): Promise<{ connections: EmailThreadCompanyConnectionWithCompany[] }> {
    return invoke("listAllPendingConnections");
  }

  async approveConnection(connectionId: string): Promise<{ connection: EmailThreadCompanyConnection }> {
    return invoke("approveConnection", { connectionId });
  }

  async rejectConnection(connectionId: string): Promise<{ connection: EmailThreadCompanyConnection }> {
    return invoke("rejectConnection", { connectionId });
  }

  async rejectGlobal(threadId: string): Promise<{ success: boolean }> {
    return invoke("rejectGlobal", { threadId });
  }

  async unrejectGlobal(threadId: string): Promise<{ success: boolean }> {
    return invoke("unrejectGlobal", { threadId });
  }
}
